import { supabase } from "@/lib/supabase";
import { formatSupabaseAccessError } from "@/lib/api/videos";
import type { CurriculumPhaseType, OrganizationCurriculumRow } from "@/types/database";

export type CreateOrganizationCurriculumInput = {
  organization_id: string;
  title: string;
  description: string | null;
  /** Required when phase_type='phase'; must be NULL for orientation/results. */
  start_date: string | null;
  education_video_id: string | null;
  exercise_bundle_id: string | null;
  phase_type: CurriculumPhaseType;
  /** Auto-computed by createOrganizationCurriculum when phase_type='phase'; ignored otherwise. */
  phase_number?: number | null;
  intro_video_id: string | null;
  outro_video_id: string | null;
  template_id: string | null;
  poster_url?: string | null;
  poster_thumbnail_url?: string | null;
  display_order?: number;
};

export type UpdateOrganizationCurriculumInput = Partial<
  Pick<
    OrganizationCurriculumRow,
    | "title"
    | "description"
    | "start_date"
    | "education_video_id"
    | "exercise_bundle_id"
    | "phase_type"
    | "intro_video_id"
    | "outro_video_id"
    | "template_id"
    | "poster_url"
    | "poster_thumbnail_url"
    | "display_order"
  >
>;

export async function listOrganizationCurriculum(organizationId: string): Promise<OrganizationCurriculumRow[]> {
  const { data, error } = await supabase
    .from("organization_curriculum")
    .select("*")
    .eq("organization_id", organizationId)
    .order("start_date", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false });
  if (error) throw new Error(formatSupabaseAccessError(error));
  return (data ?? []) as OrganizationCurriculumRow[];
}

/** Single row by id (RLS must allow caller org). Used for ?phase= playback handoff. */
export async function getOrganizationCurriculumById(id: string): Promise<OrganizationCurriculumRow | null> {
  const { data, error } = await supabase.from("organization_curriculum").select("*").eq("id", id).maybeSingle();
  if (error) throw new Error(formatSupabaseAccessError(error));
  return (data ?? null) as OrganizationCurriculumRow | null;
}

/**
 * Returns max(phase_number) for an org's phase rows, or 0 if none exist.
 * Used to assign the next phase_number on insert.
 */
export async function nextPhaseNumberForOrg(organizationId: string): Promise<number> {
  const { data, error } = await supabase
    .from("organization_curriculum")
    .select("phase_number")
    .eq("organization_id", organizationId)
    .eq("phase_type", "phase")
    .order("phase_number", { ascending: false, nullsFirst: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(formatSupabaseAccessError(error));
  const row = data as { phase_number: number | null } | null;
  return (row?.phase_number ?? 0) + 1;
}

export async function createOrganizationCurriculum(
  input: CreateOrganizationCurriculumInput,
): Promise<OrganizationCurriculumRow> {
  if (!input.education_video_id && !input.exercise_bundle_id) {
    throw new Error("Choose an education video and/or an exercise bundle.");
  }
  if (input.phase_type === "phase" && !input.start_date) {
    throw new Error("Phase rows require a start date.");
  }
  if ((input.phase_type === "orientation" || input.phase_type === "results") && input.start_date) {
    throw new Error("Orientation and Results rows must not have a start date.");
  }

  let phase_number: number | null = null;
  if (input.phase_type === "phase") {
    phase_number = input.phase_number ?? (await nextPhaseNumberForOrg(input.organization_id));
  }

  const payload = {
    organization_id: input.organization_id,
    title: input.title,
    description: input.description,
    start_date: input.start_date,
    education_video_id: input.education_video_id,
    exercise_bundle_id: input.exercise_bundle_id,
    phase_type: input.phase_type,
    phase_number,
    intro_video_id: input.intro_video_id,
    outro_video_id: input.outro_video_id,
    template_id: input.template_id,
    poster_url: input.poster_url ?? null,
    poster_thumbnail_url: input.poster_thumbnail_url ?? null,
    display_order: input.display_order ?? 0,
  };

  const { data, error } = await supabase
    .from("organization_curriculum")
    .insert(payload as never)
    .select()
    .single();
  if (error) throw new Error(formatSupabaseAccessError(error));
  return data as OrganizationCurriculumRow;
}

export async function updateOrganizationCurriculum(
  id: string,
  patch: UpdateOrganizationCurriculumInput,
): Promise<OrganizationCurriculumRow> {
  const { data, error } = await supabase
    .from("organization_curriculum")
    .update(patch as never)
    .eq("id", id)
    .select()
    .single();
  if (error) throw new Error(formatSupabaseAccessError(error));
  return data as OrganizationCurriculumRow;
}

export async function deleteOrganizationCurriculum(id: string): Promise<void> {
  const { error } = await supabase.from("organization_curriculum").delete().eq("id", id);
  if (error) throw new Error(formatSupabaseAccessError(error));
}
